import React from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';

/**
 * QuickStatsCard Component
 *
 * Individual statistics card for dashboard
 *
 * Features:
 * - Large stat value display
 * - Icon with customizable color
 * - Label and optional subtitle
 * - Hover effect
 * - Click navigation (optional)
 *
 * @component
 * @param {QuickStatsCardProps} props - Component props
 * @returns {JSX.Element} Rendered stats card
 */
interface QuickStatsCardProps {
  icon: React.ReactNode;
  value: number | string;
  label: string;
  subtitle?: string;
  color?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';
  onClick?: () => void;
}

export const QuickStatsCard: React.FC<QuickStatsCardProps> = ({
  icon,
  value,
  label,
  subtitle,
  color = 'primary',
  onClick,
}) => {
  // ===== Render =====
  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        } : undefined,
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: `${color}.main`,
            }}
          >
            {icon}
          </Box>
        </Box>

        <Typography variant="h3" component="div" gutterBottom sx={{ fontWeight: 'bold', color: `${color}.main` }}>
          {value}
        </Typography>

        <Typography variant="body1" color="text.secondary" gutterBottom>
          {label}
        </Typography>

        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
